import { forwardRef, ForwardRefRenderFunction, useEffect, useImperativeHandle, useState } from "react";
import { Col, Form, Input, Row } from "antd";
import { Trans, useTranslation } from "react-i18next";
import { useInChina } from "@app/kalodata";
import { useAppSelector } from "@hooks/redux";
import useCountDown from "@hooks/useCountDown";
import { useTiktokAuth } from "@hooks/useTiktokAuth";
import { useHideElement } from "@hooks/utils";
import report from "@report";
import ContactUs from "../ContactUs";
import { SendCode } from "../../../../components/SendCode";

type FormCaptchaHandle = {
  clearCountDown: () => {};
  validateCaptchaError: (err: string) => void;
};

interface FormCaptchaItemProps {
  form: any;
  phone: any;
  codeType: any;
  type: any;
  setCaptchaValue?: (value: string) => void;
  preconditionFn?: Function | undefined;
}

const FormCaptchaItem: ForwardRefRenderFunction<FormCaptchaItemProps, FormCaptchaHandle> = (
  { form, type, phone, setCaptchaValue, preconditionFn: _preconditionFn, codeType }: any,
  ref: any,
) => {
  const [captchaErrorMsg, setCaptchaErrorMsg] = useState<string>("");
  const [targetDate, setTargetDate] = useState<number>(0);
  const { t } = useTranslation();
  const [captchawranMsg, setCaptchawranMsg] = useState<boolean>(false);
  const [captchawranCode, setCaptchawranCode] = useState<boolean>(false);
  const { language } = useAppSelector((state) => state.globalReducer);
  const [_, forceUpdate] = useState("");
  const hasEmail = codeType === "email";
  window.hasEmail = hasEmail;
  const account = form.getFieldValue(hasEmail ? "email" : "phone");
  useInChina();

  const { tiktokLink } = useTiktokAuth("signup");

  const getPrefix = () => {
    return form.getFieldValue("prefix");
  };

  useEffect(() => {
    forceUpdate(String(new Date().getTime()) + Math.random());
    return () => {
      setTargetDate(0);
      sessionStorage.removeItem("captcha_countdown_time");
      sessionStorage.removeItem("props.config");
    };
  }, []);

  useEffect(() => {
    const captcha_countdown_time = sessionStorage.getItem("captcha_countdown_time");
    if (Number(captcha_countdown_time) > 1000) {
      setTargetDate(Date.now() + Number(captcha_countdown_time));
    }
    return () => {
      setTargetDate(0);
      setCaptchawranMsg(false);
      setCaptchawranCode(false);
      setCaptchaErrorMsg("");
    };
  }, []);

  useHideElement("captcha-btn", () => {
    setCaptchawranCode(false);
  });

  useEffect(() => {
    if (captchaErrorMsg && captchawranMsg) {
      setCaptchawranMsg(false);
      setCaptchawranCode(false);
    }
  }, [captchaErrorMsg, captchawranMsg]);

  function specialHandleID(text: string) {
    if (language === "id-ID") {
      if (hasEmail) {
        return text === "Verification code" ? "Kode via Email" : "Kirim";
      }
      if (!hasEmail) {
        return text === "Verification code" ? "Kode via WhatsApp" : "Kirim";
      }
    }
    return t(text);
  }

  const [countdown] = useCountDown({
    targetDate,
    onEnd: () => {
      sessionStorage.removeItem("captcha_countdown_time");
      setTargetDate(0);
      report.event("count_down_end", {
        kalo_value: account,
        kalo_type: hasEmail ? "email" : "sms",
      });
    },
  });

  useEffect(() => {
    if (countdown < 1000 && countdown >= 0) {
      sessionStorage.removeItem("captcha_countdown_time");
      setCaptchawranMsg(true);
    } else {
      sessionStorage.setItem("captcha_countdown_time", JSON.stringify(countdown));
    }
  }, [countdown]);

  useImperativeHandle(ref, () => ({
    validateCaptchaError: (err: any) => {
      setCaptchaErrorMsg(err?.message || err);
    },
    clearCountDown: () => {
      setTargetDate(0);
      sessionStorage.removeItem("captcha_countdown_time");
    },
  }));

  useEffect(() => {
    setCaptchawranMsg(false);
    setCaptchawranCode(false);
    setCaptchaErrorMsg("");
  }, [language]);

  return (
    <Form.Item
      validateStatus={!captchaErrorMsg ? "success" : "error"}
      help={t(captchaErrorMsg)}
      className="captcha-item"
    >
      <Row gutter={8}>
        <Col span={24}>
          <Form.Item name="captcha" noStyle>
            <Input
              size="large"
              onChange={(e) => {
                setCaptchaValue?.(e.target.value || "");
              }}
              onBlur={(e) => {
                report.event("input_captcha_blur", {
                  kalo_value: e.target.value,
                  kalo_type: hasEmail ? "email" : "sms",
                });
              }}
              onFocus={() => {
                report.event("input_captcha_focus", {
                  kalo_type: hasEmail ? "email" : "sms",
                });
              }}
              placeholder={specialHandleID("Verification code")}
              className="h-[50px]! text-[14px]! rounded-[4px]!"
              maxLength={30}
            />
          </Form.Item>
        </Col>

        {captchawranCode && <ContactUs type={type} />}
        <Col
          span={8}
          className="absolute! right-[11px]! h-[50px] leading-[50px] flex flex-col justify-center items-center text-kds-accent cursor-pointer"
        >
          <SendCode
            {...({ needCaptcha: codeType === "phone" } as any)}
            isEmail={codeType === "email"}
            isAliyunCaptcha={getPrefix() === "86"}
            scene={type}
            getValid={() => {
              return phone.validateStatus === "success";
            }}
            getData={() => {
              return codeType === "email"
                ? { email: form.getFieldValue("email") }
                : { countryCode: getPrefix(), phoneNumber: form.getFieldValue("phone") };
            }}
          ></SendCode>
        </Col>
      </Row>

      {captchawranMsg && type === "signup" && (
        <span className="wran_info relative top-1 left-0 text-[#868692]">
          <Trans
            i18nKey="signupErrorTip"
            components={{
              1: (
                <a
                  href="javascript:;"
                  onClick={() => {
                    tiktokLink("signup");
                  }}
                ></a>
              ),
              2: (
                <a
                  href="javascript:;"
                  className="contact_us"
                  onClick={(e) => {
                    e.stopPropagation();
                    report.click({
                      kalo_pos: "contact_us",
                    });

                    setCaptchawranCode(true);
                  }}
                />
              ),
            }}
          />
        </span>
      )}

      {captchawranMsg && type !== "signup" && (
        <span className="wran_info relative top-1 left-0 text-[#868692]">
          <Trans
            i18nKey="ContactUs"
            components={{
              1: (
                <a
                  className="contact_us"
                  onClick={(e) => {
                    e.stopPropagation();
                    report.click({
                      kalo_pos: "contact_us",
                      kalo_type: hasEmail ? "email" : "sms",
                    });
                    setCaptchawranCode(true);
                  }}
                >
                  Contact us
                </a>
              ),
            }}
          ></Trans>
        </span>
      )}
    </Form.Item>
  );
};

export default forwardRef(FormCaptchaItem);
